import pageConfigApi from "@/api/pageConfigApi"
import { fetchWrapper } from "@/helpers"
import { checkPagePermission } from "@/helpers/permissionHelper"
import { useAuthStore } from "@/stores"
import { defineStore } from "pinia"
import { nextTick } from "vue"

const baseUrl = `${process.env.VUE_APP_PROXY_SERVER}/api`
const pageConfigApiService = new pageConfigApi()

export const useUsersStore = defineStore(
  `${process.env.VUE_APP_NAME}_userStore`,
  {
    id: "users",
    state: () => ({
      users: [],
      user: {},
      roles: [],
      dict: [],
      pageConfigs: [],
      userProjects: [],
      lastUpdateProject: "",
    }),
    persist: true,
    actions: {
      getToken() {
        const user = JSON.parse(
          localStorage.getItem(`${process.env.VUE_APP_NAME}_user`)
        )

        return user ? user.token : null
      },
      async register(user) {
        await fetchWrapper.post(`${baseUrl}/user/register`, user)
      },
      async getAll() {
        this.users = { loading: true }
        try {
          this.users = await fetchWrapper.get(baseUrl + "/user/list")
        } catch (error) {
          this.users = { error }
        }
      },
      async getDict() {
        this.dict = { loading: true }
        try {
          this.dict = await fetchWrapper.get(`${baseUrl}/user/dict`)
        } catch (error) {
          this.dict = { error }
        }
      },
      async clearDict() {
        this.dict = []
      },
      async getById(id) {
        this.user = { loading: true }
        try {
          this.user = await fetchWrapper.get(`${baseUrl}/user/${id}`)
        } catch (error) {
          this.user = { error }
        }
      },
      async update(id, params) {
        await fetchWrapper.put(`${baseUrl}/user/${id}`, params)

        // update stored user if the logged in user updated their own record
        const authStore = useAuthStore()
        if (id === authStore.user.id) {
          // update local storage
          const user = { ...authStore.user, ...params }

          localStorage.setItem(
            `${process.env.VUE_APP_NAME}_user`,
            JSON.stringify(user)
          )

          // update auth user in pinia state
          authStore.user = user
        }
      },
      async delete(id) {
        // add isDeleting prop to user being deleted
        const user = this.users.find((x) => x.id === id)
        if (user) {
          user.isDeleting = true
        }

        await fetchWrapper.delete(`${baseUrl}/${id}`)

        // remove user from list after deleted
        this.users = this.users.filter((x) => x.id !== id)

        // auto logout if the logged in user deleted their own record
        const authStore = useAuthStore()
        if (id === authStore.user.id) {
          authStore.logout()
        }
      },
      async fetchRole() {
        this.roles = { loading: true }
        try {
          this.roles = await fetchWrapper.get(`${baseUrl}/user/AllRole`)
        } catch (error) {
          this.roles = { error }
        }
      },
      async getAllRoles() {
        return await fetchWrapper.get(baseUrl + "/user/rolelist")
      },
      async fetchPageConfigs() {
        if (this.pageConfigs.loading) return

        var checkUpdate = false
        try {
          var maxDateObject = this.pageConfigs.reduce(
            (max, item) =>
              new Date(item.updatedDateTime) > new Date(max.updatedDateTime)
                ? item
                : max,
            { updatedDateTime: 0 } // <-- initial value
          )

          checkUpdate = await pageConfigApiService.checkUpdatePageConfig(
            maxDateObject.updatedDateTime
          )
        } catch (err) {
          console.error(err)
        }

        if (!checkUpdate) {
          this.pageConfigs = { loading: true }
          try {
            const allPageConfigs = await pageConfigApiService.getAllPageConfig(
              true
            )

            this.pageConfigs = allPageConfigs.filter((x) =>
              checkPagePermission(x.permissionName)
            )
          } catch (error) {
            this.pageConfigs = { error }
          }
        }
      },
      async fetchPageConfigsByPermission() {
        this.pageConfigs = { loading: true }
        try {
          const allPageConfigs = await pageConfigApiService.getAllPageConfig(
            true
          )

          this.pageConfigs = allPageConfigs.filter((x) =>
            checkPagePermission(x.permissionName)
          )
        } catch (error) {
          this.pageConfigs = { error }
        }
      },
      async getPageConfig(id) {
        if (!id)
          throw new Error("Page config id is required to copy the page config.")

        if (this.pageConfigs.length === 0) {
          await this.fetchPageConfigs()
        }

        // When loading equal undefined it's mean page config is load done.
        if (this.pageConfigs.loading === undefined) {
          const config = this.pageConfigs.find((x) => x.id == id)

          if (!config) return

          return { ...config }
        }
      },
    },
  }
)
